/**
* Hand-written locale copy for /{locale}/store/category/$category.
* Six locales × six categories. No MT — each string is unique so the
* hreflang cluster passes Google duplicate-content de-dup.
*/
import type { StoreLocale } from "./locale-copy";

export interface CategoryLocaleCopy {
  eyebrow: string;
  h1: string; // supports {name}
  lead: string; // supports {name} + {n}
  listH2: string; // supports {n}
  perItem: string; // contact-for-quote label
  ctaHome: string;
}
export interface CategoryLocalePack {
  lang: string;
  hreflang: string;
  common: CategoryLocaleCopy;
  /** Per-category paragraph appended after lead (keyed by category id). */
  perCategory: Record<string, { name: string; blurb: string }>;
}

const cats = ["identity", "education", "immigration", "financial", "corporate", "medical-legal"] as const;

export const CATEGORY_COPY: Record<StoreLocale, CategoryLocalePack> = {
  en: {
    lang: "en", hreflang: "en",
    common: {
      eyebrow: "Store · Category",
      h1: "NAATI Translation — {name}",
      lead: "Certified translations for {n} document types in the {name} category. Quotation from our staff after document review, with a genuine NAATI translator stamp.",
      listH2: "All {n} products in this category",
      perItem: "request a quote",
      ctaHome: "← Back to Store",
    },
    perCategory: {
      "identity": { name: "Identity & Civil", blurb: "Birth, marriage, divorce, ID and household documents — the DHA / IRCC / USCIS staples." },
      "education": { name: "Education", blurb: "Transcripts, degrees, diplomas and school records for skilled-migration and Student 500 lodgements." },
      "immigration": { name: "Immigration & Vetting", blurb: "Police checks, passports and visa evidence — the compliance layer for every migration file." },
      "financial": { name: "Financial", blurb: "Bank statements, sponsorship and financial-capacity documents in the format DHA case officers expect." },
      "corporate": { name: "Corporate & Business", blurb: "Company certificates, contracts and shareholder registers for Business Innovation 188/888 and 132." },
      "medical-legal": { name: "Medical & Legal", blurb: "Medical certificates, court judgements, wills and power-of-attorney — translated by NAATI practitioners with legal experience." },
    },
  },
  zh: {
    lang: "zh-CN", hreflang: "zh-CN",
    common: {
      eyebrow: "商店 · 类别",
      h1: "NAATI 翻译 — {name}",
      lead: "{name} 类别下 {n} 种文件的 NAATI 认证翻译，逐案报价，5–7 个工作日交付，盖有正品蓝章。",
      listH2: "本类别全部 {n} 件商品",
      perItem: "咨询报价",
      ctaHome: "← 返回商店",
    },
    perCategory: {
      "identity": { name: "身份与户籍", blurb: "出生、结婚、离婚、身份证与户口本 — DHA / IRCC / USCIS 最常要求的核心文件。" },
      "education": { name: "教育学历", blurb: "成绩单、学位、文凭与学校证明，适用于技术移民与 Student 500 学生签证。" },
      "immigration": { name: "移民与背景", blurb: "无犯罪记录、护照及签证辅助材料 — 每份移民档案的合规基础。" },
      "financial": { name: "财务证明", blurb: "银行流水、担保信及财力证明，按 DHA 案件官员期望的格式呈现。" },
      "corporate": { name: "公司与商业", blurb: "公司证明、合同与股东名册，适用于 Business Innovation 188/888 与 132 类别。" },
      "medical-legal": { name: "医疗与法律", blurb: "医疗证明、法院判决、遗嘱与授权书 — 由具备法律背景的 NAATI 译员翻译。" },
    },
  },
  ja: {
    lang: "ja-JP", hreflang: "ja-JP",
    common: {
      eyebrow: "ストア · カテゴリ",
      h1: "NAATI 翻訳 — {name}",
      lead: "{name} カテゴリの {n} 種類の書類を NAATI 認証翻訳。料金は案件ごとの見積もり、5〜7 営業日、本物のブルースタンプ付き。",
      listH2: "本カテゴリの商品 {n} 点",
      perItem: "料金はお問い合わせ",
      ctaHome: "← ストアに戻る",
    },
    perCategory: {
      "identity": { name: "身分・戸籍", blurb: "出生・婚姻・離婚・ID・住民票 — DHA / IRCC / USCIS の定番書類。" },
      "education": { name: "学歴", blurb: "成績証明、学位、卒業証書 — 技術移民と Student 500 の申請に最適化。" },
      "immigration": { name: "移民・審査", blurb: "無犯罪証明、パスポート、ビザ関連書類 — すべての移民ファイルの基盤。" },
      "financial": { name: "財務", blurb: "銀行残高証明、スポンサー書類、資力証明 — DHA ケースオフィサー仕様。" },
      "corporate": { name: "会社・事業", blurb: "会社登記、契約書、株主名簿 — Business Innovation 188/888/132 向け。" },
      "medical-legal": { name: "医療・法律", blurb: "診断書、判決文、遺言、委任状 — 法律実務経験のある NAATI 翻訳者が担当。" },
    },
  },
  ko: {
    lang: "ko-KR", hreflang: "ko-KR",
    common: {
      eyebrow: "스토어 · 카테고리",
      h1: "NAATI 번역 — {name}",
      lead: "{name} 카테고리의 {n}종 문서 NAATI 인증 번역. 요금은 건별 견적, 5–7 영업일, 정품 블루 스탬프 포함.",
      listH2: "이 카테고리의 전체 상품 {n}개",
      perItem: "요금 문의",
      ctaHome: "← 스토어로 돌아가기",
    },
    perCategory: {
      "identity": { name: "신분·가족", blurb: "출생·혼인·이혼·신분증·가족관계 — DHA / IRCC / USCIS 필수 문서." },
      "education": { name: "학력", blurb: "성적표·학위·졸업장·재학증명 — 기술이민과 Student 500 비자에 최적화." },
      "immigration": { name: "이민·심사", blurb: "무범죄증명·여권·비자 증빙 — 모든 이민 파일의 기반." },
      "financial": { name: "재정", blurb: "은행 잔고·후원서·재정능력 증명 — DHA 케이스 오피서 형식." },
      "corporate": { name: "기업·사업", blurb: "회사 등기·계약서·주주명부 — Business Innovation 188/888/132 대응." },
      "medical-legal": { name: "의료·법률", blurb: "진단서·판결문·유언·위임장 — 법률 경력 NAATI 번역사가 담당." },
    },
  },
  vi: {
    lang: "vi-VN", hreflang: "vi-VN",
    common: {
      eyebrow: "Cửa hàng · Danh mục",
      h1: "Dịch NAATI — {name}",
      lead: "Dịch công chứng NAATI cho {n} loại giấy tờ trong danh mục {name}. Báo giá theo từng hồ sơ, 5–7 ngày làm việc, dấu xanh NAATI chính hãng.",
      listH2: "Toàn bộ {n} sản phẩm trong danh mục",
      perItem: "liên hệ báo giá",
      ctaHome: "← Về Cửa hàng",
    },
    perCategory: {
      "identity": { name: "Hộ tịch & Nhân thân", blurb: "Khai sinh, kết hôn, ly hôn, CCCD, hộ khẩu — bộ chứng từ chuẩn cho DHA / IRCC / USCIS." },
      "education": { name: "Học vấn", blurb: "Bảng điểm, bằng, chứng chỉ — tối ưu cho định cư tay nghề và Student 500." },
      "immigration": { name: "Di trú & Xét duyệt", blurb: "Lý lịch tư pháp, hộ chiếu, chứng từ visa — nền tảng cho mọi hồ sơ di trú." },
      "financial": { name: "Tài chính", blurb: "Sao kê, bảo lãnh, chứng minh tài chính — đúng định dạng DHA case officer mong đợi." },
      "corporate": { name: "Doanh nghiệp", blurb: "Giấy phép công ty, hợp đồng, danh sách cổ đông — cho Business Innovation 188/888/132." },
      "medical-legal": { name: "Y tế & Pháp lý", blurb: "Giấy khám, bản án, di chúc, ủy quyền — dịch bởi biên dịch NAATI có kinh nghiệm pháp lý." },
    },
  },
  id: {
    lang: "id-ID", hreflang: "id-ID",
    common: {
      eyebrow: "Toko · Kategori",
      h1: "Terjemahan NAATI — {name}",
      lead: "Terjemahan tersertifikasi NAATI untuk {n} jenis dokumen dalam kategori {name}. Penawaran harga per kasus, 5–7 hari kerja, stempel biru NAATI asli.",
      listH2: "Seluruh {n} produk dalam kategori",
      perItem: "minta penawaran",
      ctaHome: "← Kembali ke Toko",
    },
    perCategory: {
      "identity": { name: "Identitas & Sipil", blurb: "Akta lahir, nikah, cerai, KTP, KK — dokumen inti untuk DHA / IRCC / USCIS." },
      "education": { name: "Pendidikan", blurb: "Transkrip, ijazah, diploma — dioptimalkan untuk migrasi terampil dan Student 500." },
      "immigration": { name: "Imigrasi & Verifikasi", blurb: "SKCK, paspor, bukti visa — dasar setiap berkas imigrasi." },
      "financial": { name: "Keuangan", blurb: "Rekening koran, sponsor, bukti kemampuan finansial — format sesuai case officer DHA." },
      "corporate": { name: "Perusahaan & Bisnis", blurb: "Akta perusahaan, kontrak, daftar pemegang saham — untuk Business Innovation 188/888/132." },
      "medical-legal": { name: "Medis & Hukum", blurb: "Surat medis, putusan pengadilan, wasiat, kuasa — oleh penerjemah NAATI berpengalaman hukum." },
    },
  },
};

export const CATEGORY_IDS = cats;
